/**
 * MochiReal — renderer Mochi ASLI dari `mochi_rigged_web.glb` (rig UniRig/Meshy, 29 bone, tanpa morph target).
 * Ekspresi & mood digerakkan lewat POSE TULANG (sumbu dunia) + bantal `bed.glb` yang menempel di bawah kaki.
 * Antarmuka disamakan dengan MochiCanvas (prosedural) agar bisa di-swap oleh MochiPet3D / MochiPage.
 */
import React, { useRef, useMemo, useLayoutEffect, useEffect, useState, Component, Suspense } from "react";
import { Canvas, useFrame, useLoader, useThree, createPortal } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils";
import MochiCanvas, { MOOD_ALIAS, Accessory, Bow } from "./MochiCanvas";

const CLAMP = 0.26; // ~15 deg
// GLB terkompres (simplify + Draco + WebP): kucing 32MB -> 1.2MB, bantal 6.3MB -> 0.4MB
const CAT_URL = "/assets/mochi/mochi_rigged_web.min.glb";
const BED_URL = "/assets/mochi/bed.min.glb";
const draco = new DRACOLoader().setDecoderPath("/draco/");
const withDraco = (loader) => loader.setDRACOLoader(draco);

// Titik tempel aksesori (ruang model, pose diam) + bone induknya. Pusat kepala asli ~(-0.03,1.22,0.02), radius ~0.5.
const HEAD_R = 0.47; // skala aksesori prosedural (dibuat untuk kepala radius ~1.05)
const ANCHORS = {
  head: { bone: "Bone_015", pos: [-0.03, 1.22, 0.02] },
  neck: { bone: "Bone_008", pos: [-0.03, 0.74, 0.0] },
  body: { bone: "Bone_001", pos: [-0.03, 0.5, 0.0] },
};
// slot per aksesori + offset tambahan (ruang aksesori, sebelum skala) agar pas di kepala asli
const ACC_SLOT = {
  bow: ["body", [0, 0.0, 0.12], 1.25], scarf: ["neck", [0, 0.34, 0], 1.5], glasses: ["head", [0, -0.55, 0.05], 0.92],
  hat: ["head", [0, -0.6, 0], 1], crown: ["head", [0, -0.35, 0], 1], santahat: ["head", [0, -0.6, 0], 1], pecimerdeka: ["head", [0, -0.5, 0], 1],
  flower: ["head", [0.1, -0.3, 0.1], 1], kartiniflower: ["head", [-0.1, -0.3, 0.1], 1], fireworks: ["head", [0, -0.1, 0], 1.3],
  balloon: ["body", [0.4, -0.26, 0.5], 1], heartwand: ["body", [0.1, -1.7, 0.8], 1],
};

// Pemetaan bone UniRig -> bagian tubuh (diverifikasi dari posisi dunia tulang; depan model = +Z)
const B = {
  root: "Bone_000",
  spine: "Bone_009",
  neck: "Bone_008",
  head: "Bone_016",
  earR: ["Bone_025", "Bone_024"],
  earL: ["Bone_028", "Bone_027"],
  tail: ["Bone_014", "Bone_013", "Bone_012", "Bone_011", "Bone_010"],
  legFR: "Bone_019",
  legFL: "Bone_022",
  legBR: "Bone_004",
  legBL: "Bone_007",
};
// Posisi mata & mulut relatif pusat kepala (kalibrasi dari kacamata) untuk rig wajah overlay
// Dari pemindaian mesh: muka datar z≈0.506 (y 1.0–1.3), moncong z≈0.626 (y 0.83–0.93), hidung y≈0.96 → mulut tepat di bawah hidung pada permukaan moncong
const FACE = { eyeX: 0.2, eyeY: -0.095, eyeZ: 0.475, eyeR: 0.14, mouth: [0, -0.375, 0.6] };

// Pose per-mood. Sudut dalam radian pada sumbu DUNIA: x=pitch (+ = menunduk), y=yaw, z=miring.
const P = (o) => Object.assign({
  headX: 0, headZ: 0, ear: 0, tailAmp: 0.25, tailSpd: 2.5, bob: 0.03, bobSpd: 1,
  rootZ: 0, spineX: 0, squash: 1, lift: 0, shiftX: 0, nod: 0, hop: 0, shiver: 0, sway: 0, legX: 0,
  knead: 0, kneadSpd: 3, wave: 0, eyeClose: 0, mouth: 0,
}, o);
export const REAL_MOODS = {
  happy: P({ knead: 0.08, kneadSpd: 1.6 }),
  joy: P({ ear: -0.15, tailAmp: 0.45, tailSpd: 5, bob: 0.05, bobSpd: 2, sway: 0.05, knead: 0.14, kneadSpd: 2.4, mouth: 0.25 }),
  excited: P({ ear: -0.25, tailAmp: 0.55, tailSpd: 7, hop: 1, sway: 0.18, bobSpd: 3, wave: 1, mouth: 0.55 }),
  levelup: P({ ear: -0.3, tailAmp: 0.6, tailSpd: 8, hop: 1.3, sway: 0.2, bobSpd: 3, wave: 1, mouth: 0.7 }),
  pet_reaction: P({ headX: 0.12, headZ: 0.4, ear: -0.15, tailAmp: 0.35, tailSpd: 3.5, squash: 0.94, spineX: 0.1, knead: 0.3, kneadSpd: 3.2, eyeClose: 0.55, mouth: 0.3 }),
  eat: P({ headX: 0.55, nod: 0.18, spineX: 0.22, ear: 0.1, tailAmp: 0.15, bob: 0.01, mouth: 1 }),
  hungry: P({ headX: 0.28, ear: 0.35, tailAmp: 0.08, tailSpd: 1.2, bob: 0.015, mouth: 0.35 }),
  sleepy: P({ headX: 0.55, headZ: 0.18, ear: 0.5, tailAmp: 0, rootZ: 0.28, squash: 0.9, bob: 0.02, bobSpd: 0.5, eyeClose: 1 }),
  tired: P({ headX: 0.38, ear: 0.55, tailAmp: 0.05, tailSpd: 1, bob: 0.015, bobSpd: 0.7, eyeClose: 0.7 }),
  sick: P({ headX: 0.42, headZ: 0.08, ear: 0.65, tailAmp: 0, shiver: 1, bob: 0.01, eyeClose: 0.5 }),
  sad: P({ headX: 0.55, ear: 0.75, tailAmp: 0, bob: 0.01, bobSpd: 0.6, eyeClose: 0.3 }),
  dead: P({ headX: 0.3, ear: 0.85, tailAmp: 0, bob: 0, rootZ: 1.35, shiftX: 0.3, eyeClose: 1 }),
  revive: P({ ear: -0.25, tailAmp: 0.45, tailSpd: 5, hop: 0.8, sway: 0.12, bobSpd: 2.5, wave: 0.6, mouth: 0.5 }),
};
export const resolveMood = (raw) => REAL_MOODS[raw] || REAL_MOODS[MOOD_ALIAS[raw]] || REAL_MOODS.happy;

// Tekstur Meshy disimpan sebagai emissive (baseColor putih) -> pindahkan ke map agar tidak "terbakar"/pucat.
export function fixMeshyMaterial(mat, glow = 0) {
  if (!mat) return;
  if (mat.emissiveMap && !mat.userData.kkFixed) {
    mat.map = mat.emissiveMap;
    mat.map.colorSpace = THREE.SRGBColorSpace;
    if (glow > 0) { mat.emissive.set(0xffffff); mat.emissiveIntensity = glow; }
    else { mat.emissiveMap = null; mat.emissive.set(0x000000); mat.emissiveIntensity = 0; }
    mat.color.set(0xffffff);
    mat.roughness = 0.92;
    mat.metalness = 0;
    mat.userData.kkFixed = true;
    mat.needsUpdate = true;
  }
}

const _q = new THREE.Quaternion(), _w = new THREE.Quaternion(), _e = new THREE.Euler();
// Rotasi sumbu-DUNIA (ruang armature) dipetakan ke lokal bone: local = P⁻¹ · W · P · rest
export function worldAxisTarget(bone, pitch, yaw, roll, stopAt) {
  _q.identity();
  const chain = [];
  for (let p = bone.parent; p && p !== stopAt; p = p.parent) chain.push(p);
  for (let i = chain.length - 1; i >= 0; i--) _q.multiply(chain[i].quaternion);
  _w.setFromEuler(_e.set(pitch, yaw, roll));
  const target = _q.clone().invert().multiply(_w).multiply(_q).multiply(bone.userData.restQ);
  return target;
}

class RealBoundary extends Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) return <MochiCanvas {...this.props.fallbackProps} />;
    return this.props.children;
  }
}

function Bed({ theme }) {
  const gltf = useLoader(GLTFLoader, BED_URL, withDraco);
  const scene = useMemo(() => gltf.scene.clone(true), [gltf.scene]);
  useLayoutEffect(() => {
    scene.traverse((o) => { if (o.isMesh) { o.material = o.material.clone(); fixMeshyMaterial(o.material, 0.45); o.receiveShadow = true; } });
  }, [scene]);
  const target = useMemo(() => (theme?.bed ? new THREE.Color(theme.bed).lerp(new THREE.Color(0xffffff), 0.35) : new THREE.Color(0xffffff)), [theme?.bed]);
  useFrame((_, dt) => {
    const k = 1 - Math.exp(-5 * Math.min(dt || 0.016, 0.05));
    scene.traverse((o) => { if (o.isMesh) o.material.color.lerp(target, k); });
  });
  // bantal 1.9×0.52×1.7 unit; permukaan bantal ±0.40. Skala 1.35 agar Mochi (lebar ~1.4) duduk di dalam bibir bantal.
  return (
    <group position={[-0.05, -0.52, 0.03]} name="mochi-bed">
      <group scale={1.35}><primitive object={scene} /></group>
      <mesh position={[0, -0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.45, 48]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.12} />
      </mesh>
    </group>
  );
}

function RealModel({ mood, accessory, costume, dragRef, actionPulse, onReact, onTouch, lookRef, theme }) {
  const face = useRef({ close: 0, mouth: 0, meow: 0 });
  const tailBoost = useRef(0);
  const lastTouchAt = useRef(0);
  const gltf = useLoader(GLTFLoader, CAT_URL, withDraco);
  const container = useRef();
  const yawGroup = useRef();
  const rig = useRef();
  const bones = useRef(null);
  const norm = useRef(null);
  const bounce = useRef(0);
  const lastPulse = useRef(0);
  const lift = useRef(0);
  const moodRef = useRef(mood);
  const [anchors, setAnchors] = useState(null);
  useEffect(() => { moodRef.current = mood; }, [mood]);

  const scene = useMemo(() => SkeletonUtils.clone(gltf.scene), [gltf.scene]);

  useLayoutEffect(() => {
    const map = {};
    scene.traverse((o) => {
      if (o.isBone) {
        map[o.name] = o;
        if (o.userData.restQ) o.quaternion.copy(o.userData.restQ); else o.userData.restQ = o.quaternion.clone();
      }
      if (o.isMesh || o.isSkinnedMesh) {
        if (!o.material.userData.kkFixed) o.material = o.material.clone();
        fixMeshyMaterial(o.material, 0.22);
        o.castShadow = true;
        o.frustumCulled = false;
      }
    });
    const pick = (n) => map[n];
    bones.current = {
      root: pick(B.root), spine: pick(B.spine), neck: pick(B.neck), head: pick(B.head),
      earR: B.earR.map(pick).filter(Boolean), earL: B.earL.map(pick).filter(Boolean),
      tail: B.tail.map(pick).filter(Boolean), legFR: pick(B.legFR), legFL: pick(B.legFL),
      legBR: pick(B.legBR), legBL: pick(B.legBL),
    };
    // semua ukuran dihitung di ruang lokal model (aman walau efek dijalankan ulang saat scene sudah terpasang/berskala)
    scene.updateMatrixWorld(true);
    const toLocal = scene.matrixWorld.clone().invert();
    const box = new THREE.Box3().setFromObject(scene).applyMatrix4(toLocal);
    const size = box.getSize(new THREE.Vector3());
    const TARGET_H = 3.2;
    norm.current = { s: TARGET_H / (size.y || 1), minY: box.min.y };
    // anchor aksesori: Group anak bone; local = inverse(boneWorld) * sceneWorld * translasi(titik tempel)
    const anc = {};
    Object.entries(ANCHORS).forEach(([k, a]) => {
      const bone = map[a.bone]; if (!bone) return;
      let g = bone.children.find((c) => c.name === `anchor-${k}`);
      if (!g) { g = new THREE.Group(); g.name = `anchor-${k}`; bone.add(g); }
      g.matrix.copy(bone.matrixWorld).invert().multiply(scene.matrixWorld).multiply(new THREE.Matrix4().makeTranslation(...a.pos));
      g.matrix.decompose(g.position, g.quaternion, g.scale);
      anc[k] = g;
    });
    setAnchors(anc);
    if (typeof window !== "undefined" && window.location.search.includes("mochidebug")) window.__mochi = { scene, map, THREE, bones: bones.current };
  }, [scene]);

  useFrame((state, dt) => {
    const bn = bones.current, nr = norm.current;
    if (!bn || !nr || !container.current || !rig.current) return;
    const t = state.clock.elapsedTime;
    const step = Math.min(dt || 0.016, 0.1);
    const K = (rate) => 1 - Math.exp(-rate * step); // lerp bebas-framerate
    const L = (a, b, rate) => THREE.MathUtils.lerp(a, b, K(rate));
    const raw = moodRef.current;
    const nm = MOOD_ALIAS[raw] || raw;
    const tgt = resolveMood(raw);
    const isDead = nm === "dead";

    // reaksi tap / aksi -> bounce sekali
    const pulse = (actionPulse || 0) + (dragRef.current?.pulse || 0);
    if (pulse !== lastPulse.current) {
      lastPulse.current = pulse;
      if (!isDead) bounce.current = 1;
      onReact && onReact();
    }

    // napas + bounce + lompat (excited/revive)
    let by = Math.sin(t * 1.6 * tgt.bobSpd) * tgt.bob;
    let squash = tgt.squash;
    if (tgt.hop) by += Math.abs(Math.sin(t * 5.5)) * 0.28 * tgt.hop;
    if (bounce.current > 0) {
      bounce.current = Math.max(0, bounce.current - step * 2.2);
      const bb = Math.sin(bounce.current * Math.PI);
      by += bb * 0.22;
      squash *= 1 - bb * 0.07;
    }
    lift.current = L(lift.current, tgt.lift, 4);
    by += lift.current;

    // look offset (prop) + ikuti jari/kursor (hover) ± drag, semua dibatasi ±15°
    const lk = lookRef?.current || { yaw: 0, pitch: 0 };
    const hv = dragRef.current?.hover || { yaw: 0, pitch: 0 };
    const lookY = THREE.MathUtils.clamp(((lk.yaw || 0) + hv.yaw) * Math.PI / 180, -CLAMP, CLAMP);
    const lookX = THREE.MathUtils.clamp(((lk.pitch || 0) + hv.pitch) * Math.PI / 180, -CLAMP, CLAMP);
    const d = dragRef.current;
    const dragY = d && d.dragging ? THREE.MathUtils.clamp(d.targetY, -CLAMP, CLAMP) : 0;
    const dragX = d && d.dragging ? THREE.MathUtils.clamp(d.targetX, -CLAMP, CLAMP) : 0;

    // container: skala & pijakan; yawGroup: drag (kucing+bantal); rig: lompat/squash/goyang (kucing saja)
    const c = container.current;
    c.scale.setScalar(nr.s);
    c.position.y = -nr.minY * nr.s - 0.05;
    const yg = yawGroup.current;
    yg.rotation.y = L(yg.rotation.y, dragY + lookY * 0.3, 7);
    yg.rotation.x = L(yg.rotation.x, dragX * 0.5, 7);
    const r = rig.current;
    r.position.y = L(r.position.y, by, 14);
    r.position.x = L(r.position.x, tgt.shiftX, 4);
    r.scale.y = L(r.scale.y, squash, 9);
    const shiver = tgt.shiver ? Math.sin(t * 28) * 0.02 : 0;
    const sway = tgt.sway ? Math.sin(t * 5.5) * tgt.sway : 0;
    r.rotation.z = L(r.rotation.z, sway + shiver, 18);

    const pose = (bone, pitch, yaw, roll, rate = 6) => {
      if (!bone || !bone.userData.restQ) return;
      bone.quaternion.slerp(worldAxisTarget(bone, pitch, yaw, roll, scene), K(rate));
    };

    // ROOT: rebahan miring saat pingsan / sleepy lean
    pose(bn.root, 0, 0, tgt.rootZ, 4);
    // SPINE: condong ke depan (makan/elus)
    pose(bn.spine, tgt.spineX, 0, 0, 6);
    // HEAD: mood + look/drag + angguk saat makan
    const nod = tgt.nod ? Math.abs(Math.sin(t * 5)) * tgt.nod : 0;
    const idle = Math.sin(t * 0.9) * 0.04;
    pose(bn.head, tgt.headX + nod + lookX * 0.7, lookY * 0.6 + idle, tgt.headZ + (tgt.shiver ? Math.sin(t * 2.2) * 0.06 : 0), 7);
    // EARS: droop keluar-bawah (+) / tegak (-)
    bn.earR.forEach((b, i) => pose(b, tgt.ear * 0.35, 0, -tgt.ear * (i ? 0.5 : 0.9), 7));
    bn.earL.forEach((b, i) => pose(b, tgt.ear * 0.35, 0, tgt.ear * (i ? 0.5 : 0.9), 7));
    // TAIL: kibas horizontal (yaw) + sedikit terangkat (roll); makin cepat saat punggung diusap
    tailBoost.current = Math.max(0, tailBoost.current - step * 0.8);
    const tb0 = tailBoost.current;
    bn.tail.forEach((tb, i) => {
      const wag = Math.sin(t * (tgt.tailSpd + tb0 * 9) + i * 0.6) * (tgt.tailAmp + tb0 * 0.45) * (0.5 + i * 0.25);
      pose(tb, 0, wag, wag * 0.4 + (isDead ? 0 : Math.sin(t * 1.3) * 0.03), 9);
    });
    // TELINGA: kedutan sesekali (idle twitch) di atas pose mood
    const twitch = Math.pow(Math.max(0, Math.sin(t * 0.55 + 1.2)), 40) * 0.35;
    if (twitch > 0.01 && bn.earR[0]) pose(bn.earR[0], tgt.ear * 0.35 - twitch * 0.5, twitch * 0.6, -tgt.ear * 0.9, 12);
    // KAKI DEPAN: meremas (knead) bergantian / melambai saat senang
    const kn = tgt.knead ? Math.sin(t * tgt.kneadSpd) * tgt.knead : 0;
    // kedua kaki depan melambai bergantian (kanan fase 0, kiri fase π) dengan amplitudo sama
    const wvR = tgt.wave ? Math.max(0, Math.sin(t * 4.5)) * 1.3 * tgt.wave : 0;
    const wvL = tgt.wave ? Math.max(0, Math.sin(t * 4.5 + Math.PI)) * 1.3 * tgt.wave : 0;
    pose(bn.legFR, tgt.legX - kn - wvR, 0, wvR * 0.35, 6);
    pose(bn.legFL, tgt.legX + kn - wvL, 0, -wvL * 0.35, 6);
    // KAKI BELAKANG: ikut menekuk saat melompat, goyang halus saat idle
    const hopUp = tgt.hop ? Math.abs(Math.sin(t * 5.5)) * 0.35 : 0;
    pose(bn.legBR, hopUp + Math.sin(t * 1.1) * 0.03, 0, 0, 6);
    pose(bn.legBL, hopUp - Math.sin(t * 1.1) * 0.03, 0, 0, 6);
    // WAJAH: kedip acak + tutup mata sesuai mood; mulut buka saat makan/meong/senang
    const f = face.current;
    if (!f.nextBlink) f.nextBlink = t + 2.5;
    if (t > f.nextBlink) { f.blinkAt = t; f.nextBlink = t + 2.5 + Math.random() * 3.5; }
    const bt = t - (f.blinkAt || -10);
    const blink = bt < 0.22 ? Math.sin((bt / 0.22) * Math.PI) : 0;
    f.close = L(f.close, Math.max(tgt.eyeClose, isDead ? 1 : blink), 22);
    f.meow = Math.max(0, f.meow - step * 1.6);
    const chew = tgt.mouth >= 1 ? Math.abs(Math.sin(t * 5)) * 0.9 : tgt.mouth * (0.7 + 0.3 * Math.sin(t * 3));
    f.mouth = L(f.mouth, Math.max(chew, Math.sin(Math.min(1, f.meow) * Math.PI) * 0.9), 16);
  });

  const partAt = (e) => {
    const p = scene.worldToLocal(e.point.clone());
    return p.y > 0.82 ? (p.z > 0.15 ? "cheek" : "head") : p.y > 0.35 ? (p.z < 0.2 ? "back" : "body") : "paw";
  };
  // sentuhan pada model: pipi/kepala -> meong kecil (dilaporkan ke parent)
  const touch = (e) => {
    e.stopPropagation(); // model punya banyak sub-mesh: cegah event yang sama terpicu berulang
    const now = performance.now();
    if (!onTouch || now - lastTouchAt.current < 350) return;
    lastTouchAt.current = now;
    const part = partAt(e);
    face.current.meow = part === "cheek" || part === "head" ? 1 : 0.5;
    onTouch(part);
  };
  // usapan jari di punggung/badan -> ekor mengibas makin cepat
  const stroke = (e) => {
    e.stopPropagation();
    const part = partAt(e);
    if (part === "back" || part === "body") tailBoost.current = Math.min(1, tailBoost.current + 0.12);
  };

  const eating = (MOOD_ALIAS[mood] || mood) === "eat";

  return (
    <group ref={container}>
      <group ref={yawGroup}>
        <Suspense fallback={null}><Bed theme={theme} /></Suspense>
        <FoodBowl active={eating} />
        <group ref={rig}>
          <primitive object={scene} onPointerDown={touch} onPointerMove={stroke} />
          {anchors && <RealAccessory key={(ACC_SLOT[accessory] || ["head"])[0]} type={accessory} anchors={anchors} />}
          {anchors && <FaceRig anchor={anchors.head} face={face} />}
          {anchors && costume && <Costume key={costume} type={costume} anchors={anchors} />}
        </group>
      </group>
    </group>
  );
}

// Aksesori prosedural (dari MochiCanvas) di-portal ke anchor bone sehingga ikut gerak kepala/badan.
function RealAccessory({ type, anchors }) {
  const [slot, off, extra = 1] = ACC_SLOT[type] || ["head", [0, 0, 0], 1];
  const target = anchors[slot];
  if (!target) return null;
  const a = ANCHORS[slot].pos, h = ANCHORS.head.pos;
  // pusat kepala virtual relatif ke anchor (aksesori prosedural berpusat di kepala)
  const base = [h[0] - a[0], h[1] - a[1], h[2] - a[2]];
  return createPortal(
    <group position={base} scale={HEAD_R * extra}>
      <group position={off}>
        {type === "bow" ? <group position={[0, -1.3, 0]}><Bow /></group> : <Accessory type={type} />}
      </group>
    </group>,
    target
  );
}

// Mangkuk makan: muncul di depan Mochi saat mood makan, isi berkurang lalu hilang.
function FoodBowl({ active }) {
  const g = useRef(); const food = useRef(); const start = useRef(0);
  useFrame((state, dt) => {
    if (!g.current) return;
    const step = Math.min(dt || 0.016, 0.1);
    const k = 1 - Math.exp(-12 * step);
    if (active && !start.current) start.current = state.clock.elapsedTime;
    if (!active) start.current = 0;
    const sc = THREE.MathUtils.lerp(g.current.scale.x, active ? 1 : 0.0001, k);
    g.current.scale.setScalar(sc);
    g.current.visible = sc > 0.01;
    if (food.current) {
      const p = active ? THREE.MathUtils.clamp((state.clock.elapsedTime - start.current) / 3.4, 0, 1) : 0;
      const fs = active ? 1 - p * 0.7 : 1;
      food.current.scale.set(fs, Math.max(0.15, fs), fs);
      food.current.position.y = 0.1 - (1 - fs) * 0.04;
    }
  });
  return (
    <group ref={g} position={[-0.03, 0.04, 0.66]} scale={0.0001} name="food-bowl">
      <mesh position={[0, 0.05, 0]}><cylinderGeometry args={[0.17, 0.13, 0.1, 28]} /><meshStandardMaterial color="#8fc1e3" roughness={0.5} /></mesh>
      <mesh position={[0, 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}><torusGeometry args={[0.165, 0.02, 10, 28]} /><meshStandardMaterial color="#ff8a94" roughness={0.5} /></mesh>
      <group ref={food} position={[0, 0.1, 0]}>
        {[[0, 0.08, 0], [0.07, 0.05, 0.03], [-0.07, 0.05, -0.03], [0.02, 0.05, -0.08], [-0.03, 0.05, 0.08], [0.08, 0.05, -0.07], [-0.08, 0.05, 0.06]].map((p, i) => (
          <mesh key={i} position={p}><sphereGeometry args={[0.052, 10, 10]} /><meshStandardMaterial color={i % 2 ? "#a9683d" : "#c4874f"} roughness={0.8} /></mesh>
        ))}
      </group>
    </group>
  );
}

// Kostum musiman menempel di badan (anchor body = pinggul; torso ~ pusat (0,-0.05,0) relatif anchor, radius ~0.42)
function Costume({ type, anchors }) {
  const body = anchors.body, neck = anchors.neck;
  if (!body || !neck) return null;
  if (type === "rain") {
    return (
      <>
        {createPortal(
          <group position={[0, -0.02, 0]}>
            <mesh scale={[1.02, 0.95, 0.98]}><sphereGeometry args={[0.47, 28, 28, 0, Math.PI * 2, 0, Math.PI * 0.62]} /><meshStandardMaterial color="#f5c531" roughness={0.35} side={THREE.DoubleSide} /></mesh>
            <mesh position={[0, -0.02, 0.44]}><boxGeometry args={[0.05, 0.5, 0.03]} /><meshStandardMaterial color="#d9a812" roughness={0.4} /></mesh>
            {[0.12, -0.02, -0.16].map((y) => <mesh key={y} position={[0, y, 0.465]}><sphereGeometry args={[0.03, 10, 10]} /><meshStandardMaterial color="#3b3b3b" roughness={0.4} /></mesh>)}
          </group>, body)}
        {createPortal(
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.05, 1, 0.9]}><torusGeometry args={[0.36, 0.09, 12, 30]} /><meshStandardMaterial color="#e8b520" roughness={0.4} /></mesh>, neck)}
      </>
    );
  }
  if (type === "lebaran") {
    return (
      <>
        {createPortal(
          <group position={[0, -0.02, 0]}>
            <mesh scale={[1.0, 0.92, 0.96]}><sphereGeometry args={[0.46, 28, 28, 0, Math.PI * 2, 0, Math.PI * 0.6]} /><meshStandardMaterial color="#2f8f6b" roughness={0.75} side={THREE.DoubleSide} /></mesh>
            <mesh position={[0, -0.03, 0.44]}><boxGeometry args={[0.06, 0.5, 0.02]} /><meshStandardMaterial color="#f3e9c6" roughness={0.8} /></mesh>
            {[0.12, 0.0, -0.12].map((y) => <mesh key={y} position={[0, y, 0.46]}><sphereGeometry args={[0.025, 10, 10]} /><meshStandardMaterial color="#e3b23c" metalness={0.5} roughness={0.35} /></mesh>)}
            {[-0.3, 0.3].map((x) => <mesh key={x} position={[x, -0.2, 0.32]} rotation={[0.3, 0, 0]}><boxGeometry args={[0.14, 0.02, 0.1]} /><meshStandardMaterial color="#e3b23c" metalness={0.5} roughness={0.35} /></mesh>)}
          </group>, body)}
        {createPortal(
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.05, 1, 0.9]}><torusGeometry args={[0.34, 0.06, 12, 30]} /><meshStandardMaterial color="#f3e9c6" roughness={0.85} /></mesh>, neck)}
      </>
    );
  }
  if (type === "explorer") {
    return (
      <>
        {createPortal(
          <group position={[0, -0.02, 0]}>
            <mesh scale={[1.0, 0.9, 0.96]}><sphereGeometry args={[0.46, 28, 28, 0, Math.PI * 2, 0, Math.PI * 0.6]} /><meshStandardMaterial color="#b89a5b" roughness={0.85} side={THREE.DoubleSide} /></mesh>
            {[-0.2, 0.2].map((x) => <mesh key={x} position={[x, -0.1, 0.42]} rotation={[0.25, 0, 0]}><boxGeometry args={[0.16, 0.13, 0.04]} /><meshStandardMaterial color="#9c7f45" roughness={0.9} /></mesh>)}
            <mesh position={[0, 0.02, 0.45]}><boxGeometry args={[0.05, 0.42, 0.02]} /><meshStandardMaterial color="#6b5430" roughness={0.9} /></mesh>
            <mesh position={[0.26, 0.2, 0.36]}><sphereGeometry args={[0.045, 12, 12]} /><meshStandardMaterial color="#e3b23c" metalness={0.6} roughness={0.3} /></mesh>
          </group>, body)}
        {createPortal(
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.05, 1, 0.9]}><torusGeometry args={[0.35, 0.05, 12, 30]} /><meshStandardMaterial color="#c8323b" roughness={0.9} /></mesh>, neck)}
      </>
    );
  }
  if (type === "chef") {
    return (
      <>
        {createPortal(
          <group position={[0, -0.05, 0]}>
            <mesh position={[0, 0, 0.05]} scale={[0.86, 0.9, 0.92]}><sphereGeometry args={[0.46, 28, 28, -Math.PI * 0.45, Math.PI * 0.9, Math.PI * 0.12, Math.PI * 0.5]} /><meshStandardMaterial color="#fbfbfb" roughness={0.9} side={THREE.DoubleSide} /></mesh>
            <mesh position={[0, -0.08, 0.44]} rotation={[0.25, 0, 0]}><boxGeometry args={[0.22, 0.14, 0.03]} /><meshStandardMaterial color="#e9e9e9" roughness={0.9} /></mesh>
            <mesh position={[0, 0.12, 0.43]}><boxGeometry args={[0.16, 0.05, 0.02]} /><meshStandardMaterial color="#ff5a5f" roughness={0.8} /></mesh>
          </group>, body)}
        {createPortal(
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.05, 1, 0.9]}><torusGeometry args={[0.36, 0.035, 12, 30]} /><meshStandardMaterial color="#fbfbfb" roughness={0.9} /></mesh>, neck)}
      </>
    );
  }
  if (type === "gala") {
    return (
      <>
        {createPortal(
          <group position={[0, -0.02, 0]}>
            <mesh scale={[1.0, 0.92, 0.96]}><sphereGeometry args={[0.46, 28, 28, 0, Math.PI * 2, 0, Math.PI * 0.6]} /><meshStandardMaterial color="#1f2233" roughness={0.6} side={THREE.DoubleSide} /></mesh>
            <mesh position={[0, 0.02, 0.445]}><boxGeometry args={[0.16, 0.42, 0.02]} /><meshStandardMaterial color="#fbfbfb" roughness={0.8} /></mesh>
            {[0.1, -0.02, -0.14].map((y) => <mesh key={y} position={[0, y, 0.462]}><sphereGeometry args={[0.018, 8, 8]} /><meshStandardMaterial color="#1f2233" roughness={0.5} /></mesh>)}
          </group>, body)}
        {createPortal(
          <group position={[0, -0.03, 0.4]}>
            {[-0.06, 0.06].map((x) => <mesh key={x} position={[x, 0, 0]} rotation={[0, 0, x > 0 ? 0.4 : -0.4]}><boxGeometry args={[0.1, 0.06, 0.03]} /><meshStandardMaterial color="#c8323b" roughness={0.7} /></mesh>)}
            <mesh><sphereGeometry args={[0.025, 10, 10]} /><meshStandardMaterial color="#c8323b" roughness={0.7} /></mesh>
          </group>, neck)}
      </>
    );
  }
  if (type === "natal") {
    return createPortal(
      <group>
        {[[-0.05, "#c8323b"], [0.05, "#2e7d4f"]].map(([dy, col]) => (
          <mesh key={col} position={[0, dy, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[1.08, 1, 0.9]}><torusGeometry args={[0.4, 0.085, 12, 30]} /><meshStandardMaterial color={col} roughness={0.9} /></mesh>
        ))}
        <group position={[0.14, -0.28, 0.42]} rotation={[0.2, 0, 0.1]}>
          {[0, 1, 2, 3].map((i) => <mesh key={i} position={[0, -i * 0.11, 0]}><boxGeometry args={[0.18, 0.11, 0.08]} /><meshStandardMaterial color={i % 2 ? "#2e7d4f" : "#c8323b"} roughness={0.9} /></mesh>)}
          <mesh position={[0, -0.47, 0]}><boxGeometry args={[0.18, 0.06, 0.08]} /><meshStandardMaterial color="#fbfbff" roughness={0.9} /></mesh>
        </group>
      </group>, neck);
  }
  return null;
}

// Rig wajah overlay (GLB tanpa morph target): kelopak mata untuk kedip/tutup mata + mulut terbuka.
function FaceRig({ anchor, face }) {
  const lidL = useRef(); const lidR = useRef(); const mouth = useRef();
  useFrame(() => {
    const c = face.current.close, m = face.current.mouth;
    [lidL.current, lidR.current].forEach((lid, i) => {
      if (!lid) return;
      const sy = 0.14 + 0.52 * c; // kelopak gepeng di sisi atas mata, menebal/turun saat menutup
      lid.scale.set(1.1, sy, 0.3);
      lid.position.y = FACE.eyeY + FACE.eyeR * 0.98 - FACE.eyeR * sy;
      lid.rotation.z = (i ? -1 : 1) * (0.1 + 0.18 * c);
      lid.visible = c > 0.04;
    });
    if (mouth.current) { mouth.current.scale.set(1.6, Math.max(0.001, 0.22 + m * 0.7), 0.6); mouth.current.visible = m > 0.05; }
  });
  const LID = <meshStandardMaterial color="#eda66f" emissive="#eda66f" emissiveIntensity={0.28} roughness={0.9} />;
  return createPortal(
    <group>
      <mesh ref={lidL} position={[-FACE.eyeX, FACE.eyeY + FACE.eyeR, FACE.eyeZ + 0.02]}><sphereGeometry args={[FACE.eyeR, 20, 20]} />{LID}</mesh>
      <mesh ref={lidR} position={[FACE.eyeX, FACE.eyeY + FACE.eyeR, FACE.eyeZ + 0.02]}><sphereGeometry args={[FACE.eyeR, 20, 20]} />{LID}</mesh>
      <mesh ref={mouth} position={FACE.mouth}><sphereGeometry args={[0.03, 16, 16]} /><meshStandardMaterial color="#5e2632" roughness={0.6} /></mesh>
    </group>,
    anchor
  );
}

// Pencahayaan terang (siang) / redup kebiruan (malam), dilerp halus.
function Lights({ night }) {
  const amb = useRef(); const key = useRef(); const fill = useRef(); const hemi = useRef();
  useFrame((_, dt) => {
    const k = 1 - Math.exp(-3 * Math.min(dt || 0.016, 0.1));
    const T = night ? { amb: 0.32, key: 0.45, fill: 0.25, hemi: 0.25 } : { amb: 0.95, key: 1.15, fill: 0.45, hemi: 0.6 };
    if (amb.current) amb.current.intensity = THREE.MathUtils.lerp(amb.current.intensity, T.amb, k);
    if (key.current) key.current.intensity = THREE.MathUtils.lerp(key.current.intensity, T.key, k);
    if (fill.current) fill.current.intensity = THREE.MathUtils.lerp(fill.current.intensity, T.fill, k);
    if (hemi.current) hemi.current.intensity = THREE.MathUtils.lerp(hemi.current.intensity, T.hemi, k);
    const col = night ? "#9fb4ff" : "#ffffff";
    if (amb.current) amb.current.color.lerp(new THREE.Color(col), k);
  });
  return (
    <>
      <ambientLight ref={amb} intensity={0.95} />
      <directionalLight ref={key} position={[3, 6, 5]} intensity={1.15} />
      <directionalLight ref={fill} position={[-4, 2, 2]} intensity={0.45} color={night ? "#8fa7ff" : "#ffd9c9"} />
      <hemisphereLight ref={hemi} args={["#ffffff", "#c9b9a8", 0.6]} />
    </>
  );
}

// Jembatan untuk memotret kanvas (Foto Bareng Mochi)
function CaptureBridge({ captureRef }) {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    if (!captureRef) return;
    captureRef.current = () => { gl.render(scene, camera); return gl.domElement.toDataURL("image/png"); };
    return () => { if (captureRef) captureRef.current = null; };
  }, [gl, scene, camera, captureRef]);
  return null;
}

// Kamera menunduk lebih rendah di layar portrait agar kepala Mochi tidak tertutup gelembung ucapan
function CameraRig() {
  const { camera, size } = useThree();
  useEffect(() => {
    const portrait = size.width / size.height < 1;
    camera.position.set(0, 1.4, portrait ? 8.3 : 7.6);
    camera.lookAt(0, portrait ? 0.7 : 0.1, 0);
  }, [camera, size]);
  return null;
}

function RealCanvasInner({ mood, accessory, costume, actionPulse, onReact, onTouch, mini, theme, look, dragRef, night, captureRef }) {
  const lookRef = useRef({ yaw: 0, pitch: 0 });
  lookRef.current = look || lookRef.current;
  return (
    <Canvas camera={{ position: [0, 1.4, mini ? 8.4 : 7.6], fov: 38 }} dpr={[1, 2]}
      gl={{ antialias: true, alpha: true, toneMapping: THREE.NoToneMapping, preserveDrawingBuffer: true }} style={{ background: "transparent" }}>
      <CameraRig />
      <CaptureBridge captureRef={captureRef} />
      <Lights night={!!night} />
      <group position={[0, -1.35, 0]} scale={0.9}>
        <Suspense fallback={null}>
          <RealModel mood={mood} accessory={accessory} costume={costume} dragRef={dragRef} actionPulse={actionPulse} onReact={onReact} onTouch={onTouch} lookRef={lookRef} theme={theme} />
        </Suspense>
      </group>
    </Canvas>
  );
}

export default function MochiReal(props) {
  const dragRef = useRef({ dragging: false, targetX: 0, targetY: 0, startX: 0, startY: 0, moved: false, pulse: 0, hover: { yaw: 0, pitch: 0 } });
  const wrapRef = useRef();
  // kepala Mochi menoleh mengikuti jari/kursor di atas panggung (±15°)
  const hover = (e) => {
    const r = wrapRef.current?.getBoundingClientRect?.();
    if (!r || !r.width) return;
    const nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
    dragRef.current.hover = { yaw: THREE.MathUtils.clamp(nx * 15, -15, 15), pitch: THREE.MathUtils.clamp(ny * 10, -10, 10) };
  };

  const down = (e) => {
    const d = dragRef.current;
    d.dragging = true; d.moved = false; d.startX = e.clientX; d.startY = e.clientY;
    wrapRef.current?.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
    d.targetY = THREE.MathUtils.clamp(dx * 0.012, -CLAMP, CLAMP);
    d.targetX = THREE.MathUtils.clamp(dy * 0.010, -CLAMP, CLAMP);
  };
  const up = (e) => {
    const d = dragRef.current;
    if (d.dragging && !d.moved) d.pulse += 1;
    d.dragging = false;
    try { wrapRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
  };

  return (
    <RealBoundary fallbackProps={props}>
      <div ref={wrapRef}
        onPointerDown={down} onPointerMove={(e) => { move(e); hover(e); }} onPointerUp={up}
        onPointerLeave={() => { dragRef.current.dragging = false; dragRef.current.hover = { yaw: 0, pitch: 0 }; }}
        style={{ width: "100%", height: "100%", cursor: "grab", touchAction: "pan-y" }}
        data-testid="mochi-real-canvas"
      >
        <RealCanvasInner {...props} dragRef={dragRef} />
      </div>
    </RealBoundary>
  );
}
